import React from "react";
import { NavLink } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

const ICONS = {
  rom: (props) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        d="M4 6.5C4 5.12 5.12 4 6.5 4h11c1.38 0 2.5 1.12 2.5 2.5v11c0 1.38-1.12 2.5-2.5 2.5h-11C5.12 20 4 18.88 4 17.5v-11Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.5 9.75h7M8.5 14.25h3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  blocks: (props) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <rect
        x="3.75"
        y="3.75"
        width="6.5"
        height="6.5"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect
        x="13.75"
        y="3.75"
        width="6.5"
        height="6.5"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect
        x="3.75"
        y="13.75"
        width="6.5"
        height="6.5"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect
        x="13.75"
        y="13.75"
        width="6.5"
        height="6.5"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  ),
  settings: (props) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        d="m13.34 3.5.34-.34a1.5 1.5 0 0 1 2.12 0l1.04 1.04a1.5 1.5 0 0 1 0 2.12l-.34.34a7.02 7.02 0 0 1 1.28 2.77h.48a1.5 1.5 0 0 1 1.5 1.5v1.48a1.5 1.5 0 0 1-1.5 1.5h-.48a7.02 7.02 0 0 1-1.28 2.77l.34.34a1.5 1.5 0 0 1 0 2.12l-1.04 1.04a1.5 1.5 0 0 1-2.12 0l-.34-.34a7.02 7.02 0 0 1-2.77 1.28v.48a1.5 1.5 0 0 1-1.5 1.5H9.59a1.5 1.5 0 0 1-1.5-1.5v-.48a7.02 7.02 0 0 1-2.77-1.28l-.34.34a1.5 1.5 0 0 1-2.12 0L1.82 19.6a1.5 1.5 0 0 1 0-2.12l.34-.34a7.02 7.02 0 0 1-1.28-2.77H.4A1.5 1.5 0 0 1-1.1e-15 12.9V11.4a1.5 1.5 0 0 1 1.5-1.5h.48a7.02 7.02 0 0 1 1.28-2.77l-.34-.34a1.5 1.5 0 0 1 0-2.12L5.96 3.62a1.5 1.5 0 0 1 2.12 0l.34.34a7.02 7.02 0 0 1 2.77-1.28V2.2A1.5 1.5 0 0 1 12.7.7h1.48a1.5 1.5 0 0 1 1.5 1.5v.48a7.02 7.02 0 0 1 2.77 1.28Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  ),
};

const logoForTheme = {
  light: `${process.env.PUBLIC_URL}/assets/kropz_logo_for_light_mode.png`,
  dark: `${process.env.PUBLIC_URL}/assets/kropz_logo_for_dark_mode.png`,
};

const partnerLogoForTheme = {
  light: `${process.env.PUBLIC_URL}/assets/dri_logo_for_light_mode.png`,
  dark: `${process.env.PUBLIC_URL}/assets/dri_logo_for_dark_mode.png`,
};

function Sidebar({ items, isOpen, onToggle, onNavigate, isMobile }) {
  const { theme } = useTheme();
  const isCollapsed = !isOpen && !isMobile;
  const LogoIcon = ({ icon }) => {
    const IconComponent = ICONS[icon];
    if (!IconComponent) return null;
    return <IconComponent className="sidebar__icon" />;
  };

  const sidebarClassName = [
    "sidebar",
    isOpen ? "sidebar--expanded" : "sidebar--collapsed",
    isCollapsed ? "sidebar--icon-only" : "",
    isMobile ? "sidebar--mobile" : "sidebar--desktop",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <aside
      id="app-sidebar"
      className={sidebarClassName}
      aria-label="Primary"
      aria-hidden={isMobile && !isOpen}
      data-collapsed={isCollapsed || undefined}
    >
      <div className="sidebar__inner">
        <div className={`sidebar__brand ${isCollapsed ? "sidebar__brand--collapsed" : ""}`}>
          {isMobile && (
            <button
              type="button"
              className="sidebar__dismiss"
              onClick={onToggle}
              aria-label="Close navigation"
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
          {!isCollapsed && (
            <img
              src={logoForTheme[theme]}
              alt="Kropz"
              className="sidebar__logo"
              draggable="false"
            />
          )}
        </div>
        <nav className="sidebar__nav" aria-label="Main navigation">
          <ul>
            {items.map((item) => (
              <li key={item.id}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `sidebar__link ${isActive ? "is-active" : ""}`
                  }
                  onClick={onNavigate}
                  tabIndex={isMobile && !isOpen ? -1 : undefined}
                  title={item.label}
                  aria-label={isCollapsed ? item.label : undefined}
                >
                  <span className="sidebar__link-indicator" aria-hidden="true" />
                  <LogoIcon icon={item.icon} />
                  <span
                    className="sidebar__link-label"
                    aria-hidden={isCollapsed}
                  >
                    {item.label}
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        {!isCollapsed && (
          <div className="sidebar__footer">
            <span className="sidebar__powered">powered by</span>
            <img
              src={partnerLogoForTheme[theme]}
              alt="DigiRock Innovations"
              className="sidebar__partner-logo"
              draggable="false"
            />
          </div>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
